// Cockpit daily-ops surface — four KPI tiles + drill drawers + simplified
// Service Requests view + previous-period timeline. Rendered inline inside
// AdminCockpit's DashboardView, below the Day-1 Onboarding panel.
//
// Numbers are backend-served (deterministic) via /api/lifecycle/cockpit/*.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartPulse, CircleDashed, FileWarning, ArrowRight, Eye,
  Loader2, CheckCircle2, Send, ShieldAlert, PackageOpen, RefreshCw, Recycle,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

// ============================================================================
// Four daily-ops tiles — Fleet health · Dark assets · Warranty · Shelf life
// ============================================================================
export function CockpitDailyOpsStrip({ region = "emea", brandBlue, brandSoft, onOpenDexRisk }) {
  const [data, setData] = useState(null);
  const [darkOpen, setDarkOpen] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [warrantyOpen, setWarrantyOpen] = useState(false);
  const [shelfOpen, setShelfOpen] = useState(false);
  const navigate = useNavigate();

  // Re-fetch on region switch so the tiles visibly rescale.
  useEffect(() => {
    endpoints.cockpitDailyOps(region).then(setData).catch(() => {});
  }, [region]);

  if (!data) {
    return (
      <div className="rounded-2xl p-6 bg-white flex items-center gap-2 text-slate-500 text-sm" style={{ border: `1px solid ${brandSoft}` }}>
        <Loader2 size={14} className="animate-spin" /> loading daily ops…
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="cockpit-daily-ops">
        <OpsTile
          testid="cockpit-tile-fleet"
          icon={<HeartPulse size={16} />}
          eyebrow="Fleet health"
          headline={`${data.fleet_health.flagged_total}`}
          suffix="flagged"
          sub={`DEX ${data.fleet_health.dex_flagged} · Battery ${data.fleet_health.battery_risk}`}
          onClick={() => onOpenDexRisk && onOpenDexRisk()}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="warn"
          spark={[6, 8, 7, 9, 11, 10, 12, 11]}
        />
        <OpsTile
          testid="cockpit-tile-dark"
          icon={<CircleDashed size={16} />}
          eyebrow="Dark assets"
          headline={`${data.dark_assets.count}`}
          suffix="silent"
          sub={`No signal · ${data.dark_assets.threshold_days}d+`}
          onClick={() => setDarkOpen(true)}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="warn"
        />
        <OpsTile
          testid="cockpit-tile-warranty"
          eyebrow="Warranty expiring"
          icon={<ShieldAlert size={16} />}
          headline={`${data.warranty_expiring.total}`}
          suffix="units"
          sub={`30d ${data.warranty_expiring.buckets[0].count} · 60d ${data.warranty_expiring.buckets[1].count} · 90d ${data.warranty_expiring.buckets[2].count}`}
          onClick={() => setWarrantyOpen(true)}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="info"
        />
        <OpsTile
          testid="cockpit-tile-shelf"
          eyebrow="Shelf life"
          icon={<PackageOpen size={16} />}
          headline={`${data.shelf_life.avg_days}d`}
          suffix="avg on shelf"
          sub={`${data.shelf_life.aged_count} aged >${data.shelf_life.threshold_days}d · ${data.shelf_life.total_on_shelf} on shelf`}
          onClick={() => setShelfOpen(true)}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="info"
        />
        <OpsTile
          testid="cockpit-tile-invoices"
          icon={<FileWarning size={16} />}
          eyebrow="Invoices on hold"
          headline={`${data.invoices_on_hold.count}`}
          suffix={`· $${(data.invoices_on_hold.total_usd / 1000).toFixed(0)}K held`}
          sub={`Top · ${data.invoices_on_hold.top_reason}`}
          onClick={() => setInvoicesOpen(true)}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="warn"
        />
        <OpsTile
          testid="cockpit-tile-refresh"
          icon={<RefreshCw size={16} />}
          eyebrow="Refresh candidates"
          headline={`${data.refresh_candidates.count}`}
          suffix="to review"
          sub={`${data.refresh_candidates.aged_over_4y} over 4 yrs old`}
          onClick={() => navigate("/lifecycle?stage=refresh")}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="info"
        />
        <OpsTile
          testid="cockpit-tile-leavers"
          icon={<Recycle size={16} />}
          eyebrow="Leavers to recover"
          headline={`${data.leavers_pending.count}`}
          suffix="devices"
          sub={`${data.leavers_pending.due_7d} leaving in 7 days`}
          onClick={() => navigate("/recovery")}
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          tone="info"
        />
      </div>

      <DarkAssetsDrawer key={`dark-${region}`} region={region} open={darkOpen} onClose={() => setDarkOpen(false)} brandBlue={brandBlue} brandSoft={brandSoft} />
      <InvoicesDrawer key={`inv-${region}`} region={region} open={invoicesOpen} onClose={() => setInvoicesOpen(false)} brandBlue={brandBlue} brandSoft={brandSoft} />
      <WarrantyDrawer key={`war-${region}`} region={region} open={warrantyOpen} onClose={() => setWarrantyOpen(false)} brandBlue={brandBlue} brandSoft={brandSoft} />
      <ShelfLifeDrawer key={`shelf-${region}`} region={region} open={shelfOpen} onClose={() => setShelfOpen(false)} brandBlue={brandBlue} brandSoft={brandSoft} />
    </>
  );
}

// Compact tile — tinted icon badge (same language as the attention row), one
// big number, one line of context. Only Fleet health carries a trend line.
function OpsTile({ testid, icon, eyebrow, headline, suffix, sub, onClick, brandBlue, brandSoft, spark, tone = "info" }) {
  const t = tone === "warn" ? { bg: "#FEF3C7", fg: "#B45309" } : { bg: brandSoft, fg: brandBlue };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="group rounded-2xl bg-white p-4 text-left transition-shadow hover:shadow-md"
      style={{ border: `1px solid ${brandSoft}` }}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: t.bg, color: t.fg }}>
            {icon}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight line-clamp-2">{eyebrow}</div>
        </div>
        <Eye size={11} className="opacity-30 group-hover:opacity-100 transition-opacity text-slate-500 shrink-0" />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-bold tracking-tight text-slate-900 tabular-nums">{headline}</span>
        <span className="text-[11px] text-slate-500">{suffix}</span>
      </div>
      {spark && <Sparkline points={spark} color={t.fg} />}
      <div className="text-[11px] text-slate-500 mt-1.5 truncate">{sub}</div>
    </button>
  );
}

// Tiny inline sparkline — SVG, no library.
function Sparkline({ points, color }) {
  const W = 100, H = 20;
  const max = Math.max(...points), min = Math.min(...points);
  const range = Math.max(1, max - min);
  const step = W / (points.length - 1);
  const coords = points.map((v, i) => `${i * step},${H - ((v - min) / range) * H}`).join(" ");
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} className="mt-1.5" preserveAspectRatio="none">
      <polyline points={coords} fill="none" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" opacity={0.6} />
    </svg>
  );
}

// Shelf-life drawer — ageing device list, oldest first, with status chip.
function ShelfLifeDrawer({ region, open, onClose, brandBlue, brandSoft }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (open && !data) endpoints.cockpitShelfLife(80, region).then(setData).catch(() => {}); }, [open, data, region]);
  const [filter, setFilter] = useState("all");
  const list = data?.list || [];
  const filtered = filter === "aged" ? list.filter((d) => d.days_on_shelf > (data?.threshold_days || 30)) : list;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Shelf life · {data ? `${data.avg_days}d avg` : "…"}</SheetTitle>
          <SheetDescription>
            {data ? <>
              <b className="text-slate-900">{data.total_on_shelf}</b> devices on shelf · <b className="text-slate-900">{data.aged_count}</b> aged past {data.threshold_days} days
            </> : "loading…"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-1 mt-4" data-testid="shelf-filter-tabs">
          {[
            { id: "all",  label: `All (${list.length})` },
            { id: "aged", label: `Aged >${data?.threshold_days || 30}d (${list.filter((d) => d.days_on_shelf > (data?.threshold_days || 30)).length})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className="text-xs font-semibold h-8 px-3 rounded-md transition-colors"
              style={filter === f.id ? { backgroundColor: brandBlue, color: "#fff" } : { color: "#64748B", border: "1px solid #E2E8F0" }}
              data-testid={`shelf-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-2" data-testid="shelf-device-list">
          {!data && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/>loading…</div>}
          {filtered.map((d) => {
            const aged = d.days_on_shelf > (data?.threshold_days || 30);
            return (
              <div key={d.asset_tag} className="rounded-lg p-3 flex items-center gap-3 text-[11.5px]" style={{ border: `1px solid ${brandSoft}` }}>
                <span className="font-mono font-bold text-slate-900 shrink-0">{d.asset_tag}</span>
                <span className="text-slate-500 flex-1 truncate">{d.model} · {d.site}</span>
                <span
                  className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0"
                  style={aged ? { backgroundColor: "#FEF3C7", color: "#B45309" } : { backgroundColor: "#ECFDF5", color: "#0F766E" }}
                >
                  {aged ? "ageing" : "healthy"}
                </span>
                <span className="font-mono tabular-nums text-slate-700 shrink-0 w-10 text-right">{d.days_on_shelf}d</span>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function WarrantyDrawer({ region, open, onClose, brandBlue, brandSoft }) {
  const [data, setData] = useState(null);
  const [activeBucket, setActiveBucket] = useState("30d");
  useEffect(() => { if (open && !data) endpoints.cockpitWarrantyExpiring(region).then(setData).catch(() => {}); }, [open, data, region]);
  const bucket = data?.buckets?.find((b) => b.id === activeBucket) || data?.buckets?.[0];
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Warranty expiring · {data?.total || "…"}</SheetTitle>
          <SheetDescription>Pre-empt the next refresh wave. Click a bucket to see the devices going OOW in that window.</SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 mt-4" data-testid="warranty-bucket-tabs">
          {data?.buckets?.map((b) => (
            <button
              key={b.id}
              onClick={() => setActiveBucket(b.id)}
              className="flex-1 rounded-lg p-3 transition-all text-left"
              style={
                b.id === activeBucket
                  ? { backgroundColor: brandBlue, color: "#FFF" }
                  : { backgroundColor: "#F8FBFF", border: `1px solid ${brandSoft}`, color: "#0F172A" }
              }
              data-testid={`warranty-bucket-${b.id}`}
            >
              <div className="text-[9.5px] font-bold uppercase tracking-wider opacity-80">{b.label}</div>
              <div className="text-2xl font-bold tabular-nums">{b.count}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-2" data-testid="warranty-device-list">
          {bucket?.list?.map((d) => (
            <div key={d.asset_tag} className="rounded-lg p-3 flex items-center gap-3 text-[11.5px]" style={{ border: `1px solid ${brandSoft}` }}>
              <span className="font-mono font-bold text-slate-900 shrink-0">{d.asset_tag}</span>
              <span className="text-slate-500 flex-1 truncate">{d.model} · {d.business_unit}</span>
              <span className="text-slate-700 shrink-0">{d.oem}</span>
              <span className="font-mono tabular-nums text-slate-700 shrink-0">{d.days_to_expiry}d</span>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DarkAssetsDrawer({ region, open, onClose, brandBlue, brandSoft }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (open && !data) endpoints.cockpitDarkAssets(50, region).then(setData).catch(() => {}); }, [open, data, region]);
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Dark assets · {data?.count || "…"}</SheetTitle>
          <SheetDescription>
            Devices in CMDB with no DEX pulse, no login event, and no ITSM touch in the last {data?.threshold_days || 60} days.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-2" data-testid="dark-assets-list">
          {!data && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/>loading…</div>}
          {data?.list?.map((d) => (
            <div key={d.asset_tag} className="rounded-lg p-3 flex items-center gap-3" style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#FFF" }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[12px] font-bold text-slate-900">{d.asset_tag}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: `${brandBlue}14`, color: brandBlue }}>{d.suggested_action}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                  {d.user_of_record} · {d.business_unit} · {d.model}
                </div>
              </div>
              <div className="text-right text-[11px] text-slate-500">
                <div className="font-mono tabular-nums">{d.last_seen_days}d silent</div>
                <div className="text-slate-400">last {d.last_seen}</div>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function InvoicesDrawer({ region, open, onClose, brandBlue, brandSoft }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (open && !data) endpoints.cockpitInvoicesOnHold(region).then(setData).catch(() => {}); }, [open, data, region]);
  const action = (inv) => toast.success(`${inv.action} queued for ${inv.invoice_id}`);
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Invoices on hold · {data?.count || "…"}</SheetTitle>
          <SheetDescription>
            {data ? <>${(data.total_usd / 1000).toFixed(1)}K held · top reason <b className="text-slate-900">{data.top_reason}</b></> : "loading…"}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-3" data-testid="invoices-list">
          {data?.list?.map((inv) => (
            <div key={inv.invoice_id} className="rounded-xl p-4" style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#FFF" }}>
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div>
                  <div className="font-mono text-[12px] font-bold text-slate-900">{inv.invoice_id}</div>
                  <div className="text-[11px] text-slate-500">{inv.vendor} · PO {inv.po}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-slate-900 tabular-nums">${inv.amount_usd.toLocaleString()}</div>
                </div>
              </div>
              <div className="text-[11.5px] mb-2">
                <span className="font-semibold text-amber-700">{inv.reason}</span>
                <span className="text-slate-500 ml-1">· {inv.reason_detail}</span>
              </div>
              <Button
                size="sm"
                onClick={() => action(inv)}
                className="text-white hover:opacity-90 h-8 text-[11px]"
                style={{ backgroundColor: brandBlue }}
              >
                {inv.action === "Release" ? <CheckCircle2 size={11} className="mr-1.5" /> : <Send size={11} className="mr-1.5" />}
                {inv.action}
              </Button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}


// ============================================================================
// Service Requests (REQ) — compact list + single summary strip
// ============================================================================
// Lightweight replacement for the old multi-column stacked chart. Shows a
// short list of top open REQs plus one small counts-by-business strip.
// Incidents are intentionally hidden on this page.
const PRI_COLORS = { p1: "#DC2626", p2: "#F59E0B", p3: "#60A5FA" };

export function CockpitOpenTickets({ region = "emea", brandBlue, brandSoft }) {
  const [groupBy, setGroupBy] = useState("operating_line");
  const [data, setData] = useState(null);
  const [modalGroup, setModalGroup] = useState(null);
  const [topTickets, setTopTickets] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    endpoints.cockpitOpenTickets(groupBy, region).then(setData).catch(() => {});
  }, [groupBy, region]);

  // Fetch top REQs across all columns on mount (ordered by SLA ascending).
  useEffect(() => {
    let cancelled = false;
    setTopTickets([]);
    (async () => {
      try {
        const columns = (await endpoints.cockpitOpenTickets("operating_line", region)).columns || [];
        const details = await Promise.all(columns.map((c) => endpoints.cockpitOpenTicketsDetail(c.id, region)));
        const all = details.flatMap((d, i) => (d.tickets || []).map((t) => ({ ...t, group_id: columns[i].id, group_label: columns[i].label })));
        all.sort((a, b) => a.sla_hrs_left - b.sla_hrs_left);
        if (!cancelled) setTopTickets(all.slice(0, 6));
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, [region]);

  const totalAll = useMemo(() => (data?.columns || []).reduce((n, c) => n + c.p1 + c.p2 + c.p3, 0), [data]);
  const maxCol = useMemo(() => Math.max(1, ...(data?.columns || []).map((c) => c.p1 + c.p2 + c.p3)), [data]);

  const openModal = (group) => setModalGroup(group);

  return (
    <div className="rounded-2xl bg-white p-5 md:p-6" style={{ border: `1px solid ${brandSoft}` }} data-testid="cockpit-open-tickets">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">Service requests</div>
          <div className="text-lg font-semibold text-slate-900 mt-0.5">
            <span className="tabular-nums">{totalAll}</span> open REQs · new hires, swaps, access
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[
            { id: "operating_line", label: "By operating line" },
            { id: "legal_entity",   label: "By legal entity"   },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setGroupBy(t.id)}
              className="h-8 px-3 rounded-md text-[11px] font-semibold transition-colors"
              style={groupBy === t.id ? { backgroundColor: brandBlue, color: "#fff" } : { color: "#64748B", border: "1px solid #E2E8F0" }}
              data-testid={`tickets-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Compact single-line summary strip — counts per business */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="tickets-summary-strip">
        {(data?.columns || []).map((col) => {
          const total = col.p1 + col.p2 + col.p3;
          const widthPct = Math.round((total / maxCol) * 100);
          return (
            <button
              key={col.id}
              onClick={() => openModal(col)}
              data-testid={`tickets-col-${col.id}`}
              className="group flex-1 min-w-[150px] rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm"
              style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#FFF" }}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-600 truncate">{col.label}</div>
                <div className="text-[13px] font-bold text-slate-900 tabular-nums shrink-0 ml-2">{total}</div>
              </div>
              <div className="h-1.5 w-full rounded-full overflow-hidden flex" style={{ backgroundColor: "#F1F5F9" }}>
                <div style={{ width: `${(col.p1 / total) * widthPct}%`, backgroundColor: PRI_COLORS.p1 }} />
                <div style={{ width: `${(col.p2 / total) * widthPct}%`, backgroundColor: PRI_COLORS.p2 }} />
                <div style={{ width: `${(col.p3 / total) * widthPct}%`, backgroundColor: PRI_COLORS.p3 }} />
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[9.5px] text-slate-500 tabular-nums">
                <span><b className="text-red-600">{col.p1}</b> P1</span>
                <span><b className="text-amber-600">{col.p2}</b> P2</span>
                <span><b className="text-sky-600">{col.p3}</b> P3</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Short list of top open REQs — SLA-ascending, click to open modal */}
      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${brandSoft}` }} data-testid="top-reqs-list">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
          <span>Top open REQs · earliest SLA first</span>
          <span>{topTickets.length} shown</span>
        </div>
        <div>
          {topTickets.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-slate-400">loading…</div>
          )}
          {topTickets.map((t) => (
            <button
              key={t.ticket_id}
              onClick={() => openModal({ id: t.group_id, label: t.group_label })}
              className="w-full flex items-center gap-3 px-3 py-2 text-left border-t border-slate-100 first:border-t-0 hover:bg-slate-50 transition-colors text-[11.5px]"
              data-testid={`top-req-${t.ticket_id}`}
            >
              <span className="font-mono font-bold text-slate-900 shrink-0">{t.ticket_id}</span>
              <span
                className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0"
                style={{ backgroundColor: `${PRI_COLORS[t.priority.toLowerCase()]}22`, color: PRI_COLORS[t.priority.toLowerCase()] }}
              >
                {t.priority}
              </span>
              <span className="text-slate-700 flex-1 truncate">{t.summary}</span>
              <span className="text-slate-500 shrink-0 hidden md:inline">{t.group_label}</span>
              <span className="font-mono tabular-nums text-slate-500 shrink-0 w-14 text-right">{t.sla_hrs_left}h SLA</span>
            </button>
          ))}
        </div>
      </div>

      <TicketModal region={region} group={modalGroup} onClose={() => setModalGroup(null)} onPushedToDemand={() => navigate("/lifecycle?stage=plan")} brandBlue={brandBlue} brandSoft={brandSoft} />
    </div>
  );
}

function TicketModal({ region, group, onClose, onPushedToDemand, brandBlue, brandSoft }) {
  const open = !!group;
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  useEffect(() => {
    if (!open) { setData(null); setSel({}); return; }
    endpoints.cockpitOpenTicketsDetail(group.id, region).then(setData).catch(() => {});
  }, [open, group?.id, region]);

  const toggle = (tid) => setSel((s) => ({ ...s, [tid]: !s[tid] }));
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const toggleAll = () => {
    const all = data?.tickets || [];
    if (selectedIds.length === all.length) setSel({});
    else setSel(Object.fromEntries(all.map((t) => [t.ticket_id, true])));
  };
  const push = async () => {
    if (!selectedIds.length) return;
    try {
      const r = await endpoints.cockpitPushToDemand(selectedIds);
      toast.success(`Pushed ${r.added.length} ticket(s) to Demand ad-hoc intake`);
      onClose();
      onPushedToDemand && onPushedToDemand();
    } catch (e) { toast.error("Push failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="tickets-modal">
        <DialogHeader>
          <DialogTitle>{group?.label} · open service requests</DialogTitle>
          <DialogDescription>Select the REQs you want folded into the next Demand run. One click bulk-pushes them into the agent's Ad-hoc intake.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" onChange={toggleAll} checked={!!data?.tickets?.length && selectedIds.length === data.tickets.length} />
                </th>
                <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-500 text-[9.5px]">Ticket</th>
                <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-500 text-[9.5px]">Pri</th>
                <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-500 text-[9.5px]">User</th>
                <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-500 text-[9.5px]">Summary</th>
                <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-slate-500 text-[9.5px]">SLA</th>
              </tr>
            </thead>
            <tbody>
              {data?.tickets?.map((t) => (
                <tr key={t.ticket_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-2 py-1.5"><input type="checkbox" checked={!!sel[t.ticket_id]} onChange={() => toggle(t.ticket_id)} /></td>
                  <td className="px-2 py-1.5 font-mono text-slate-700">{t.ticket_id}</td>
                  <td className="px-2 py-1.5"><span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${PRI_COLORS[t.priority.toLowerCase()]}22`, color: PRI_COLORS[t.priority.toLowerCase()] }}>{t.priority}</span></td>
                  <td className="px-2 py-1.5 text-slate-700">{t.user}</td>
                  <td className="px-2 py-1.5 text-slate-700">{t.summary}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{t.sla_hrs_left}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between pt-3 text-[11.5px] text-slate-600">
          <span><b className="text-slate-900">{selectedIds.length}</b> of {data?.tickets?.length || 0} selected</span>
          <Button
            onClick={push}
            disabled={!selectedIds.length}
            size="sm"
            className="text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: brandBlue }}
            data-testid="push-to-demand-btn"
          >
            <Send size={11} className="mr-1.5" />
            Push {selectedIds.length} to Demand
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Previous-period delivery timeline (Q3 · Q4 · Dec live)
// ============================================================================
export function CockpitPeriodsTimeline({ region = "emea", brandBlue, brandSoft }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { endpoints.cockpitPeriods(region).then(setData).catch(() => {}); }, [region]);
  return (
    <div className="rounded-2xl bg-white p-5 md:p-6" style={{ border: `1px solid ${brandSoft}` }} data-testid="cockpit-periods">
      <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-1">Delivery timeline</div>
      <div className="text-lg font-semibold text-slate-900 mb-4">Prior periods vs this month — the engine is continuous, not a one-shot</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {data?.periods?.map((p) => (
          <button
            key={p.id}
            onClick={() => setOpenId(p.id)}
            data-testid={`period-tile-${p.id}`}
            className="group rounded-xl p-4 text-left transition-all hover:shadow-md"
            style={{
              border: p.status === "active" ? `2px solid ${brandBlue}` : `1px solid ${brandSoft}`,
              backgroundColor: p.status === "active" ? `${brandBlue}0A` : "#FFF",
              boxShadow: p.status === "active" ? `0 0 0 3px ${brandBlue}1F` : "none",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: p.status === "active" ? brandBlue : "#64748B" }}>{p.label}</div>
              <span className={`text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-full`} style={p.status === "active" ? { backgroundColor: brandBlue, color: "#FFF" } : { backgroundColor: "#F1F5F9", color: "#64748B" }}>
                {p.status}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <MiniNum label="Ordered"    value={p.ordered} />
              <MiniNum label="Delivered"  value={p.delivered} />
              <MiniNum label="Recovered"  value={p.recovered} />
              <MiniNum label="SLA hit"    value={`${p.sla_hit_pct}%`} />
            </div>
            <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
              <ArrowRight size={10} /> click for cohort
            </div>
          </button>
        ))}
      </div>

      <PeriodDrawer region={region} id={openId} onClose={() => setOpenId(null)} brandBlue={brandBlue} brandSoft={brandSoft} />
    </div>
  );
}

function MiniNum({ label, value }) {
  return (
    <div>
      <div className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-bold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function PeriodDrawer({ region, id, onClose, brandBlue, brandSoft }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (!id) { setData(null); return; } endpoints.cockpitPeriodCohort(id, region).then(setData).catch(() => {}); }, [id, region]);
  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{data?.period?.label || "…"} · cohort</SheetTitle>
          <SheetDescription>
            {data?.period ? <>
              Ordered <b className="text-slate-900">{data.period.ordered}</b> · Delivered <b className="text-slate-900">{data.period.delivered}</b> · Recovered <b className="text-slate-900">{data.period.recovered}</b> · SLA <b className="text-slate-900">{data.period.sla_hit_pct}%</b>
            </> : "loading cohort…"}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-2" data-testid="period-cohort-list">
          {data?.cohort?.map((d, i) => (
            <div key={i} className="rounded-lg p-3 flex items-center gap-3 text-[11.5px]" style={{ border: `1px solid ${brandSoft}` }}>
              <span className="font-mono font-bold text-slate-900">{d.asset_tag}</span>
              <span className="text-slate-500 flex-1 truncate">{d.model}</span>
              <span className="text-slate-700 tabular-nums">${d.cost_usd}</span>
              {d.recovered && <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${brandBlue}14`, color: brandBlue }}>recovered · ${d.resale_usd}</span>}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
